import React, { useLayoutEffect, useRef } from 'react';
import { withActiveAnchor } from '../../platform';

import {
	ARCHIVE,
	BELL,
	CARD_ICON,
	CLOSE,
	COLLECTION_ICON,
	DOWNLOAD,
	DUPLICATE,
	EDIT,
	EYE,
	FUNNEL,
	HOME,
	KEYBOARD,
	LIGHTNING,
	LIST,
	OPEN,
	PAPERCLIP,
	PLAY,
	PIN_ANGLE,
	RATING_STAR_FILL,
	RATING_STAR_OUTLINE,
	RELATE,
	SETTINGS_COG,
	SHARE,
	TAG_ICON,
	TRASH,
 PRINTER,} from '../icons';

/**
 * The platform action taxonomy.
 *
 * Every context menu on a record — collection, card definition, item, group —
 * is built from the same groups, in this order, each rendered as a
 * labeled section with a divider between:
 *
 *   navigate     Go somewhere else: open the record, its card definition, its
 *                collection, its settings.
 *   record       Operate on the record where you are: duplicate, relate,
 *                attach, favorite, change who can see it. An item's card
 *                actions (KPR-551) lead it as ONE entry, Actions, whose
 *                submenu lists every action of the card (KPR-1342).
 *   share        Send it elsewhere: share links, export files.
 *   help         Learn the surface you are on: its keyboard shortcuts today,
 *                more later. Added to every PAGE's ⋯ by ContextBar itself,
 *                so no page has to remember to offer it.
 *   destructive  Remove something: delete, remove-from, archive. Always
 *                last, always red, always behind a confirmation.
 *
 * Each action also carries a glyph from ACTION_GLYPHS so the same verb looks
 * the same in every menu. Surfaces describe actions as plain objects
 * ({ key, label, group, glyph?, icon?, onClick, disabled?, hidden? }) and
 * hand them to buildActionMenu, which returns the antd `menu` prop.
 *
 * An action may carry `children` — a submenu of its own ({ key, label,
 * onClick, icon?, disabled?, unavailable? }) — for a verb whose object is not
 * the record itself: one of its fields ("Filter by…" → the row's filterable
 * values), a duplicate profile, one of its card's actions. The parent then
 * has no onClick of its own; the leaves do, and they fire through the same
 * dispatcher.
 *
 * `unavailable` (KPR-1342) is a leaf (or, since KPR-710, a top-level action) that cannot run here, with its reason:
 * drawn muted with the reason as a line under its label, and marked
 * aria-disabled — NOT antd-disabled, which would take it out of the keyboard's
 * reach and leave a screen reader nothing to read (a disabled menu item cannot
 * take focus, KPR-1211). Its onClick still fires: the surface says the reason
 * again rather than running anything.
 */

export const ACTION_GROUPS = [
	{ key: 'navigate', label: 'Go to' },
	// A card's own actions (KPR-551) are ONE Record entry, Actions, since
	// KPR-1342: a group of their own put a button per action in every menu.
	{ key: 'record', label: 'Record' },
	{ key: 'share', label: 'Share & export' },
	{ key: 'help', label: 'Help' },
	{ key: 'destructive', label: 'Danger' },
];

export const ACTION_GLYPHS = {
	open: OPEN,
	edit: EDIT,
	card: CARD_ICON,
	walk: PLAY,
	collection: COLLECTION_ICON,
	home: HOME,
	items: LIST,
	settings: SETTINGS_COG,
	duplicate: DUPLICATE,
	relate: RELATE,
	attach: PAPERCLIP,
	visibility: EYE,
	filter: FUNNEL,
	favorite: RATING_STAR_OUTLINE,
	favoriteOn: RATING_STAR_FILL,
	quickAdd: LIGHTNING,
	// Pin to / unpin from the signed-in Home page (a per-user shortcut —
	// never the collection tier's "Pin for everyone", which is a manager's
	// promote and keeps the collection glyph).
	pin: PIN_ANGLE,
	// Follow / unfollow one item (KPR-391): hear when it changes.
	follow: BELL,
	// Tag… (one item, its ⋯) and Tags… (a selection) — docs/plans/tags.md §5.2.
	tag: TAG_ICON,
	share: SHARE,
	export: DOWNLOAD,
	print: PRINTER,
	shortcuts: KEYBOARD,
	delete: TRASH,
	remove: CLOSE,
	archive: ARCHIVE,
};

const GROUP_ORDER = new Map(ACTION_GROUPS.map((group, index) => [group.key, index]));

/**
 * The label of a leaf that cannot run here (KPR-1342): its name, and the
 * reason as a line under it. It also marks its menu entry aria-disabled, so a
 * screen reader says it is unavailable as well as why. rc-menu writes the
 * entry's aria-disabled from antd's `disabled` alone — which would also make
 * it unfocusable — so the label sets it on its entry after every render and
 * puts it back when it goes (the same entry may become available).
 */
function UnavailableLabel({ label, reason }) {
	const ref = useRef(null);
	useLayoutEffect(() => {
		const entry = ref.current ? ref.current.closest('[role="menuitem"]') : null;
		if (!entry) return undefined;
		entry.setAttribute('aria-disabled', 'true');
		return () => { entry.setAttribute('aria-disabled', 'false'); };
	});
	return (
		<span ref={ref} className="action-menu__reason-item">
			<span className="action-menu__reason-label">{label}</span>
			<span className="action-menu__reason">{reason}</span>
		</span>
	);
}

const resolveIcon = (action) => {
	if (action.icon) return action.icon;
	const Glyph = action.glyph ? ACTION_GLYPHS[action.glyph] : null;
	return Glyph ? <Glyph /> : undefined;
};

/**
 * Turns a flat list of actions into an antd Dropdown `menu`: grouped in
 * taxonomy order, labeled, divided, with destructive entries marked danger.
 * Unknown groups fall in with `record`. Hidden actions are dropped; an empty
 * result returns null so callers can skip the trigger entirely.
 */
export function buildActionMenu(actions = [], { onClick, showGroupLabels = true } = {}) {
	const visible = (actions || []).filter(action => action && !action.hidden);
	if (!visible.length) return null;

	const byKey = new Map();
	visible.forEach(action => {
		byKey.set(action.key, action);
		(action.children || []).forEach(child => { if (child?.key) byKey.set(child.key, child); });
	});
	const items = [];

	ACTION_GROUPS.forEach((group) => {
		const members = visible.filter(action => (
			(GROUP_ORDER.has(action.group) ? action.group : 'record') === group.key
		));
		if (!members.length) return;
		if (items.length) items.push({ type: 'divider', key: `divider-${group.key}` });
		const children = members.map(action => ({
			key: action.key,
			// A top-level entry that cannot run here (KPR-710) says why in the menu
			// itself, as a leaf does: a title alone is out of a touch's reach.
			label: action.unavailable ? <UnavailableLabel label={action.label} reason={action.unavailable} /> : action.label,
			...(action.unavailable ? { className: 'action-menu__unavailable' } : {}),
			icon: resolveIcon(action),
			danger: group.key === 'destructive' || Boolean(action.danger),
			disabled: Boolean(action.disabled),
			title: action.title,
			...(action.children?.length ? {
				// antd draws a submenu in a popup outside the root menu, so the
				// root's `action-menu` class never reaches it; this one does,
				// and the touch-floor rule in identity.css selects it (KPR-39).
				popupClassName: 'action-menu__sub',
				children: action.children.map(child => (child.unavailable ? {
					key: child.key,
					label: <UnavailableLabel label={child.label} reason={child.unavailable} />,
					icon: child.icon,
					className: 'action-menu__unavailable',
				} : {
					key: child.key,
					label: child.label,
					icon: child.icon,
					disabled: Boolean(child.disabled),
					title: child.title,
				})),
			} : {}),
		}));
		if (showGroupLabels) {
			items.push({ type: 'group', key: `group-${group.key}`, label: group.label, children });
		} else {
			items.push(...children);
		}
	});

	return {
		className: 'action-menu',
		items,
		onClick: ({ key, domEvent }) => {
			domEvent?.stopPropagation?.();
			const action = byKey.get(key);
			if (!action) return;
			if (typeof action.onClick === 'function') withActiveAnchor(domEvent?.currentTarget, () => action.onClick());
			if (typeof onClick === 'function') onClick(key, action);
		},
	};
}

/** Convenience: the glyph element for a verb, for buttons outside a menu. */
export function actionGlyph(name) {
	const Glyph = ACTION_GLYPHS[name];
	return Glyph ? <Glyph /> : null;
}
